<template>
  <div class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
    <div :dir="isRtl === true ? 'rtl' : 'ltr'" class="bg-white dark:bg-gray-800 rounded-lg p-6 w-11/12 max-w-3xl max-h-[80vh] overflow-y-auto">
      <h2 class="text-xl font-semibold mb-4">{{ titleText }}</h2>
      <div class="text-xs text-gray-500 dark:text-gray-400 mb-4">{{ lastUpdatedText }}</div>
      <div class="prose prose-sm dark:prose-invert max-w-none" :class="isRtl === true ? 'text-right' : 'text-left'" v-html="html"></div>
      <div class="flex justify-end mt-4">
        <button @click="$emit('close')" class="btn">{{ closeText }}</button>
      </div>
    </div>
  </div>
</template>
<script setup>
import { ref, onMounted, computed } from 'vue';
import { useI18n } from '@/i18n';
import { globalState } from '@/globalState';
import { marked } from 'marked';
import { sanitizeHtml } from '@/shared/utils/sanitizeHtml';
defineEmits(['close'])
const html = ref('');
const { currentLanguage } = useI18n();
const isRtl = computed(() => {
  try {
    const fromI18n = (currentLanguage?.value !== undefined ? currentLanguage.value : '').toString().toLowerCase();
    if (fromI18n === 'he' || fromI18n === 'ar' || (typeof fromI18n === 'string' && (fromI18n.startsWith('he') === true || fromI18n.startsWith('ar') === true))) {
      return true;
    }
    const fromGlobal = (globalState?.orgLanguage !== undefined ? globalState.orgLanguage : '').toString().toLowerCase();
    if (fromGlobal === 'he' || fromGlobal === 'ar' || fromGlobal.startsWith('he') === true || fromGlobal.startsWith('ar') === true) {
      return true;
    }
    const htmlLang = (document?.documentElement?.lang !== undefined ? document.documentElement.lang : '').toString().toLowerCase();
    if (htmlLang === 'he' || htmlLang === 'ar' || htmlLang.startsWith('he') === true || htmlLang.startsWith('ar') === true) {
      return true;
    }
    const navLang = (navigator?.language !== undefined ? navigator.language : '').toString().toLowerCase();
    if (navLang === 'he' || navLang === 'ar' || navLang.startsWith('he') === true || navLang.startsWith('ar') === true) {
      return true;
    }
  } catch (error) {
  }
  return false;
});
const titleText = computed(() => {
  const lang = getPreferredLanguageShort();
  if (lang === 'he') { return 'מדיניות פרטיות'; }
  if (lang === 'ar') { return 'سياسة الخصوصية'; }
  if (lang === 'es') { return 'Política de privacidad'; }
  if (lang === 'it') { return 'Informativa sulla privacy'; }
  return 'Privacy Policy';
});
const lastUpdatedText = computed(() => {
  const lang = getPreferredLanguageShort();
  if (lang === 'he') { return 'עודכן לאחרונה: 28 אוג׳ 2025'; }
  if (lang === 'ar') { return 'آخر تحديث: 28 آب 2025'; }
  if (lang === 'es') { return 'Última actualización: 28 Ago 2025'; }
  if (lang === 'it') { return 'Ultimo aggiornamento: 28 Ago 2025'; }
  return 'Last updated: 28 Aug 2025';
});
const closeText = computed(() => {
  const lang = getPreferredLanguageShort();
  if (lang === 'he') { return 'סגור'; }
  if (lang === 'ar') { return 'إغلاق'; }
  if (lang === 'es') { return 'Cerrar'; }
  if (lang === 'it') { return 'Chiudi'; }
  return 'Close';
});
function getPreferredLanguageCode(){
  try {
    const fromI18n = (currentLanguage?.value !== undefined ? currentLanguage.value : '').toString().toLowerCase();
    if (fromI18n === 'he' || fromI18n === 'en' || fromI18n === 'es' || fromI18n === 'it' || fromI18n === 'ar') {
      return fromI18n;
    }
    if (typeof fromI18n === 'string' && fromI18n.length >= 2) {
      const short = fromI18n.substring(0, 2);
      if (short === 'he' || short === 'en' || short === 'es' || short === 'it' || short === 'ar') {
        return short;
      }
    }
    const fromGlobal = (globalState?.orgLanguage !== undefined ? globalState.orgLanguage : '').toString().toLowerCase();
    if (fromGlobal === 'he' || fromGlobal === 'en' || fromGlobal === 'es' || fromGlobal === 'it' || fromGlobal === 'ar') {
      return fromGlobal;
    }
    if (typeof fromGlobal === 'string' && fromGlobal.length >= 2) {
      const short2 = fromGlobal.substring(0, 2);
      if (short2 === 'he' || short2 === 'en' || short2 === 'es' || short2 === 'it' || short2 === 'ar') {
        return short2;
      }
    }
    const htmlLang = (document?.documentElement?.lang !== undefined ? document.documentElement.lang : '').toString().toLowerCase();
    if (htmlLang === 'he' || htmlLang === 'en' || htmlLang === 'es' || htmlLang === 'it' || htmlLang === 'ar') {
      return htmlLang;
    }
    if (typeof htmlLang === 'string' && htmlLang.length >= 2) {
      const short3 = htmlLang.substring(0, 2);
      if (short3 === 'he' || short3 === 'en' || short3 === 'es' || short3 === 'it' || short3 === 'ar') {
        return short3;
      }
    }
    const navLang = (navigator?.language !== undefined ? navigator.language : '').toString().toLowerCase();
    if (navLang === 'he' || navLang === 'en' || navLang === 'es' || navLang === 'it' || navLang === 'ar') {
      return navLang;
    }
    if (typeof navLang === 'string' && navLang.length >= 2) {
      const short4 = navLang.substring(0, 2);
      if (short4 === 'he' || short4 === 'en' || short4 === 'es' || short4 === 'it' || short4 === 'ar') {
        return short4;
      }
    }
  } catch (error) {
  }
  return 'en';
}
async function loadPolicy(){
  const lang = getPreferredLanguageCode();
  const candidates = [`/policies/privacy.${lang}.md`,`/policies/privacy.${lang.substring(0,2)}.md`,`/policies/privacy.en.md`];
  let content = '';
  for (let i = 0; i < candidates.length; i = i + 1) {
    const url = candidates[i];
    try {
      const res = await fetch(url);
      if (res?.ok === true) {
        const txt = await res.text();
        content = txt;
        break;
      }
    } catch (e) {
    }
  }
  if (content.length > 0) {
    html.value = sanitizeHtml(marked.parse(content));
  } else {
    html.value = '';
  }
}
function getPreferredLanguageShort(){
  const full = getPreferredLanguageCode();
  if (typeof full === 'string' && full.length >= 2) { return full.substring(0,2); }
  return 'en';
}
const renderer = new marked.Renderer();
marked.setOptions({ renderer, gfm: true, breaks: true });

onMounted(() => {
  loadPolicy();
})
</script>
<style scoped>
.prose {
  line-height: 1.6;
}
</style>